import { useAxios } from './useFetch';
import { useGet } from './templateHooks';

import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
  BandwidthNumbers,
  UsesBandwidth,
  BandwidthNumberPurchase,
  BandwidthNumberDelete,
  BandwidthAccountDelete,
  ConvMarkAsRead,
  ConvTextInteraction,
  ConvSendText,
  Conversation,
  BandwidthMpsGeneric,
  MoveBandwidthNumber,
  BandwidthDemo,
  OutreachCompletionResponse,
} from 'src/@types/outReach';
import { ROS_URL, FAST_API_URL } from 'src/config';
import { Project } from 'src/@types/project';

// Only text and text-survey can be estimated by the outreach server
function outreachCanEstimate(project: Project) {
  switch (project.outreach_type) {
    case 'text-survey':
    case 'text':
      // fetch estimates for text and test-survey from outreach server
      return true;
    default:
      // do not fetch estimates for non-text projects - outreach server can't do that (yet)
      return false;
  }
}

export const useOutreachEstimate = (project: Project, config = {}) =>
  useGet<OutreachCompletionResponse, { project_id: string }>({
    queryKey: ['estimates', project.id],
    endpoint: '/bandwidth/estimate',
    url: ROS_URL,
    params: { project_id: project.id },
    config: {
      staleTime: 0,
      enabled: !!project && outreachCanEstimate(project),
      ...config,
    },
  });

export const useBandwidthNumbers = (config = {}) =>
  useGet<BandwidthNumbers>({
    queryKey: ['bandwidthNumbers'],
    endpoint: '/bandwidth/numbers',
    config: config,
    url: ROS_URL,
  });

export const useConversations = (config = {}) =>
  useGet<Conversation[]>({
    queryKey: ['conversations'],
    endpoint: '/v1/conversations',
    config: config,
  });

export const useConvTextInteractions = () => {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<ConvTextInteraction[], { voter_id: string }>({
    endpoint: '/v1/conversations/fetch-and-mark-read',
    method: 'post',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['convUnreadCount'] });
    },
  });
};

export const useConvMarkAllRead = () => {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<undefined, undefined>({
    endpoint: '/v1/conversations/mark-all-read',
    method: 'post',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['convUnreadCount'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useConvUnreadCount = (config = {}) =>
  useGet<number>({
    queryKey: ['convUnreadCount'],
    endpoint: '/v1/conversations/unread-count',
    config: config,
  });

export const useUsesBandwidth = () => {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<undefined, UsesBandwidth>({
    endpoint: '/bandwidth/org_uses_bandwidth',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['org', result] });
    },
  });
};

export const usePurchaseBandwidthNumber = () => {
  //  const queryClient = useQueryClient();
  const backendOutreachFn = useAxios<undefined, BandwidthNumberPurchase>({
    endpoint: '/bandwidth/purchase_number',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: ['bandwidthNumbers'] });
    // },
  });
};

export const useBandwidthDemo = () => {
  const backendOutreachFn = useAxios<undefined, BandwidthDemo>({
    endpoint: '/bandwidth/demo',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
  });
};

export const useConvSendText = () => {
  const backendOutreachFn = useAxios<undefined, ConvSendText>({
    endpoint: '/bandwidth/conv_send_text',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
  });
};

export const useConvMarkAsRead = () => {
  const queryClient = useQueryClient();
  const backendOutreachFn = useAxios<undefined, ConvMarkAsRead>({
    endpoint: '/bandwidth/mark_as_read',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['convUnreadCount'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useDeleteBandwidthAccount = () => {
  const mutationFn = useAxios<undefined, BandwidthAccountDelete>({
    endpoint: '/bandwidth/delete',
    method: 'delete',
    url: ROS_URL,
  });
  return useMutation({ mutationFn });
};

export const useDeleteBandwidthNumber = () => {
  const queryClient = useQueryClient();
  const backendOutreachFn = useAxios<undefined, BandwidthNumberDelete>({
    endpoint: '/bandwidth/delete_number',
    method: 'delete',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bandwidthNumbers'] });
      queryClient.invalidateQueries({ queryKey: ['projects-detail'] });
    },
  });
};

export const useBandwidthRate = () => {
  const mutationFn = useAxios<undefined, BandwidthMpsGeneric>({
    endpoint: '/v1/bandwidth-texting-mps',
    method: 'put',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn });
};

export const useBandwidthMMSRate = () => {
  const mutationFn = useAxios<undefined, BandwidthMpsGeneric>({
    endpoint: '/v1/bandwidth-texting-mms-mps',
    method: 'put',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn });
};

export const useBandwidthMoveNumber = () => {
  const queryClient = useQueryClient();
  const backendOutreachFn = useAxios<undefined, MoveBandwidthNumber>({
    endpoint: '/bandwidth/move-number',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({
    mutationFn: backendOutreachFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bandwidthNumbers'] });
    },
  });
};
